import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from '@/components/LocalizedLink';
import { useLanguage } from '@/contexts/LanguageContext';
import MagneticButton from '@/components/MagneticButton';
import { ArrowRight } from 'lucide-react';

type CTASectionProps = {
  primaryText?: string;
  primaryUrl?: string;
  secondaryText?: string;
  secondaryUrl?: string;
};

const CTASection = ({ primaryText, primaryUrl, secondaryText, secondaryUrl }: CTASectionProps) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const resolvedPrimaryUrl = primaryUrl || '/contact';
  const resolvedSecondaryUrl = secondaryUrl || '/projects';
  const isPrimaryExternal = resolvedPrimaryUrl.startsWith('http');
  const isSecondaryExternal = resolvedSecondaryUrl.startsWith('http');

  return (
    <section ref={ref} className="section-y-compact relative overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-foreground rounded-[2rem] p-10 sm:p-12 lg:p-16 text-center overflow-hidden"
        >
          {/* Studio light sweep — primary */}
          <motion.div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background:
                'linear-gradient(120deg, transparent 0%, transparent 30%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.08) 55%, transparent 70%, transparent 100%)',
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPosition: ['-100% 0%', '200% 0%'] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatDelay: 3,
              ease: [0.45, 0, 0.55, 1],
            }}
          />

          {/* Studio light sweep — secondary */}
          <motion.div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background:
                'linear-gradient(100deg, transparent 0%, transparent 35%, rgba(255,255,255,0.04) 48%, rgba(255,255,255,0.08) 52%, rgba(255,255,255,0.04) 55%, transparent 65%, transparent 100%)',
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPosition: ['-100% 0%', '200% 0%'] }}
            transition={{
              duration: 3.5,
              repeat: Infinity,
              repeatDelay: 4,
              delay: 2,
              ease: [0.45, 0, 0.55, 1],
            }}
          />

          {/* Ambient gradient orbs */}
          <motion.div
            className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-background/[0.04] blur-[80px]"
            animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-background/[0.04] blur-[80px]"
            animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Top decorative tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative z-10 mb-8"
          >
            <span className="inline-block font-body text-[10px] tracking-[0.3em] text-background/40 uppercase border border-background/10 rounded-full px-5 py-2">
              {t('cta.tag')}
            </span>
          </motion.div>

          <div className="relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-background leading-[1.1] max-w-4xl mx-auto"
            >
              {t('cta.title')}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="font-body text-background/50 mt-8 max-w-xl mx-auto leading-relaxed text-base lg:text-lg"
            >
              {t('cta.desc')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 flex flex-wrap justify-center gap-4"
            >
              <MagneticButton>
                {isPrimaryExternal ? (
                  <a
                    href={resolvedPrimaryUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group font-body text-sm px-10 py-4 bg-background text-foreground rounded-full font-medium hover:bg-background/90 transition-all duration-300 inline-flex items-center gap-2"
                  >
                    {primaryText || t('cta.button')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                ) : (
                  <Link
                    to={resolvedPrimaryUrl}
                    className="group font-body text-sm px-10 py-4 bg-background text-foreground rounded-full font-medium hover:bg-background/90 transition-all duration-300 inline-flex items-center gap-2"
                  >
                    {primaryText || t('cta.button')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                )}
              </MagneticButton>
              <MagneticButton>
                {isSecondaryExternal ? (
                  <a
                    href={resolvedSecondaryUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-body text-sm px-10 py-4 border border-background/20 text-background rounded-full font-medium hover:border-background/40 hover:bg-background/5 transition-all duration-300 inline-block"
                  >
                    {secondaryText || t('cta.secondary')}
                  </a>
                ) : (
                  <Link
                    to={resolvedSecondaryUrl}
                    className="font-body text-sm px-10 py-4 border border-background/20 text-background rounded-full font-medium hover:border-background/40 hover:bg-background/5 transition-all duration-300 inline-block"
                  >
                    {secondaryText || t('cta.secondary')}
                  </Link>
                )}
              </MagneticButton>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
